/**
 * 企微批量加好友 - 接口定义
 *
 * 失败处理已由 request.ts 的响应拦截器统一覆盖：
 * 参数校验失败是 HTTP 200 + code=422，业务失败是 HTTP 422，两者都会被 reject 并弹 msg，
 * 故业务代码无需自行判 code。分页参数用 page_size（渠道账号接口用的是 per_page，勿混）
 */
import { apiGet, apiPost, apiRequest } from '@/utils/request';
import { parsePaginatedResponse } from '@/utils/apiResponse';
import { DEFAULT_PAGE_SIZE } from '@/constants/api';
import type {
  QywxFriendConfig,
  QywxFriendItem,
  QywxFriendParseResult,
  QywxFriendStatsParams,
  QywxFriendStatsResponse,
  QywxFriendTask,
  QywxFriendTaskPayload,
} from '@/types/qywxAddFriend';

export const qywxAddFriendApi = {
  /** 企微看板：按企微账号统计主动加好友后的转化漏斗 */
  stats: (params: QywxFriendStatsParams) =>
    apiGet<QywxFriendStatsResponse>('/qywx/friend/stat', params),

  /** 渠道限速配置，用于建任务页预估耗时与提交前的乘积校验 */
  config: () => apiGet<QywxFriendConfig>('/qywx/friend/config'),

  /** 解析导入的 Excel，只解析不落库；文件里没有任何可用手机号时后端返回 422 */
  parseImport: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiPost<QywxFriendParseResult>('/qywx/friend/parse-import', form);
  },

  /** 任务列表，支持 name / status / send_type 筛选 */
  tasks: (params: Record<string, unknown> = {}) =>
    apiGet('/qywx/friend/tasks', { page: 1, page_size: DEFAULT_PAGE_SIZE, ...params }).then((d) =>
      parsePaginatedResponse<QywxFriendTask>(d),
    ),

  /** 任务详情，比列表项多返回 daily_limit / min_interval / per_minute */
  taskShow: (id: number) => apiGet<QywxFriendTask>('/qywx/friend/tasks/show', { id }),

  /** 任务明细，支持 status / phone / account_id 筛选 */
  taskItems: (params: { task_id: number } & Record<string, unknown>) =>
    apiGet('/qywx/friend/tasks/items', {
      page: 1,
      page_size: DEFAULT_PAGE_SIZE,
      ...params,
    }).then((d) => parsePaginatedResponse<QywxFriendItem>(d)),

  /** 创建任务，返回的任务对象额外带 input_count / duplicate_count 供回显导入结果 */
  taskCreate: (data: QywxFriendTaskPayload) =>
    apiPost<QywxFriendTask>('/qywx/friend/tasks/create', data),

  /** 取消任务，仅「待执行 / 执行中」可取消；已下发的无法撤回，返回被作废的待下发条数 */
  taskCancel: (id: number) =>
    apiPost<{ id: number; cancelled_count: number }>('/qywx/friend/tasks/cancel', { id }),

  /** 删除任务（软删），执行中的任务必须先取消再删 */
  taskDelete: (id: number) =>
    apiRequest('/qywx/friend/tasks/delete', { method: 'DELETE', data: { id } }),
};
